import { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider, useTheme } from './ThemeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Categories from './pages/Categories';
import Products from './pages/Products';
import Sales from './pages/Sales';
import TableOrders from './pages/TableOrders';
import Purchases from './pages/Purchases';
import Suppliers from './pages/Suppliers';
import Reports from './pages/Reports';
import Users from './pages/Users';
import BulkImport from './pages/BulkImport';
import Export from './pages/Export';
import Shortcuts from './pages/Shortcuts';
import QuickNotes from './pages/QuickNotes';
import Shifts from './pages/Shifts';
import Chat from './pages/Chat';
import Calendar from './pages/Calendar';
import NavIcon from './components/NavIcon';

const ALL_NAV = [
  { id: 'dashboard',  label: 'Dashboard',   icon: 'dashboard',   section: 'Overview',      public: false },
  { id: 'sales',      label: 'Sales',        icon: 'sales',       section: 'Transactions',  public: false },
  { id: 'quicknotes', label: 'Quick Notes',  icon: 'document',    section: null,            public: false, requires: 'sales' },
  { id: 'tableorders',label: 'Table Orders', icon: 'tableorders', section: null,            public: false, requires: 'sales' },
  { id: 'purchases',  label: 'Purchases',    icon: 'purchases',   section: null,            public: false },
  { id: 'products',   label: 'Products',     icon: 'products',    section: 'Catalogue',     public: false },
  { id: 'categories', label: 'Categories',   icon: 'categories',  section: null,            public: false },
  { id: 'shortcuts',  label: 'Shortcuts',    icon: 'clipboard',   section: null,            public: false, requires: 'products' },
  { id: 'suppliers',  label: 'Suppliers',    icon: 'suppliers',   section: 'People',        public: false },
  { id: 'reports',    label: 'Reports',      icon: 'reports',     section: 'Analytics',     public: false },
  { id: 'chat',       label: 'Team Chat',    icon: 'chat',        section: 'Communication', public: true  },
  { id: 'calendar',   label: 'Calendar',     icon: 'calendar',    section: null,            public: true  },
  { id: 'users',      label: 'Users',        icon: 'users',       section: 'Admin',         public: false },
  { id: 'shifts',     label: 'Shifts',       icon: 'clock',       section: null,            public: false, requires: 'users' },
  { id: 'bulkimport', label: 'Bulk Import',  icon: 'bulkimport',  section: null,            public: false },
  { id: 'export',     label: 'Export',       icon: 'export',      section: null,            public: false, requires: 'reports' },
];

const PAGES = {
  dashboard: Dashboard, sales: Sales, purchases: Purchases, tableorders: TableOrders,
  products: Products, categories: Categories, suppliers: Suppliers,
  reports: Reports, users: Users, bulkimport: BulkImport, export: Export,
  quicknotes: QuickNotes, shortcuts: Shortcuts, shifts: Shifts,
  chat: Chat, calendar: Calendar,
};

// ── Theme toggle switch ────────────────────────────────────
function ThemeSwitch() {
  const { isDark, toggle } = useTheme();
  return (
    <label className="theme-switch" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <input type="checkbox" checked={!isDark} onChange={toggle} />
      <span className="theme-switch-track">
        <span className="theme-switch-thumb" />
      </span>
    </label>
  );
}

// ── Change password modal ──────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirm) {
      const { default: toast } = await import('react-hot-toast');
      toast.error('Passwords do not match'); return;
    }
    setSaving(true);
    try {
      const { api } = await import('./api');
      await api.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      const { default: toast } = await import('react-hot-toast');
      toast.success('Password changed');
      onClose();
    } catch (err) {
      const { default: toast } = await import('react-hot-toast');
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Change Password</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handle} className="modal-body">
          <div className="field">
            <label>Current Password</label>
            <input type="password" value={form.currentPassword}
              onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))} required />
          </div>
          <div className="field">
            <label>New Password</label>
            <input type="password" value={form.newPassword}
              onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))} required />
          </div>
          <div className="field">
            <label>Confirm New Password</label>
            <input type="password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main app shell ─────────────────────────────────────────
function AppShell() {
  const { user, loading, logout, can } = useAuth();
  const { isDark } = useTheme();
  const [page, setPage]               = useState('dashboard');
  const [showPwModal, setShowPwModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:'100vh', color:'var(--muted)' }}>Loading…</div>
  );

  if (!user) return <Login />;

  // Chat is visible to every authenticated user; other pages respect permissions
  const nav = ALL_NAV.filter(item => item.public || can(item.requires || item.id));
  const activePage = nav.find(n => n.id === page) ? page : (nav[0]?.id || 'dashboard');
  const Page = PAGES[activePage];

  const goToPage = (id) => { setPage(id); setSidebarOpen(false); };

  return (
    <div className="app">
      {/* Mobile-only top bar with hamburger toggle */}
      <div className="mobile-topbar">
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">☰</button>
        <div className="mobile-topbar-brand">StoreBook</div>
        <div style={{ width: 36 }} />
      </div>

      {/* Backdrop, tap to close the drawer */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <h1>StoreBook</h1>
          <p>Inventory</p>
        </div>

        <nav className="sidebar-nav">
          {nav.map(item => (
            <div key={item.id}>
              {item.section && <div className="nav-section">{item.section}</div>}
              <button
                className={`nav-item ${activePage === item.id ? 'active' : ''}`}
                onClick={() => goToPage(item.id)}
              >
                <span className="icon"><NavIcon name={item.icon} /></span>
                {item.label}
              </button>
            </div>
          ))}
        </nav>

        <div className="sidebar-user" onClick={() => setShowUserMenu(v => !v)}>
          <div className="user-avatar sm">
            {(user.displayName || user.username)[0].toUpperCase()}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user.displayName}</div>
            <div className="sidebar-user-role">{user.isAdmin ? 'Administrator' : 'Staff'}</div>
          </div>
          <span style={{ marginLeft:'auto', fontSize:10, color:'var(--muted)' }}>▾</span>

          {showUserMenu && (
            <div className="user-dropdown" onClick={e => e.stopPropagation()}>
              <div className="theme-toggle-row">
                <span className="theme-toggle-label">
                  <NavIcon name={isDark ? 'moon' : 'sun'} size={15} />
                  <span>{isDark ? 'Dark mode' : 'Light mode'}</span>
                </span>
                <ThemeSwitch />
              </div>
              <div className="user-dropdown-divider" />
              <button onClick={() => { setShowPwModal(true); setShowUserMenu(false); }}>
                <NavIcon name="key" size={14} /> Change Password
              </button>
              <button onClick={logout} style={{ color:'var(--danger)' }}>
                ↩ Sign Out
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className={`main ${activePage === 'chat' ? 'main-chat' : ''}`}>
        {Page ? <Page /> : (
          <div className="empty" style={{ marginTop: 80 }}>
            <p>No pages available. Contact your administrator.</p>
          </div>
        )}
      </main>

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            fontSize: 13,
            boxShadow: 'var(--shadow)',
          },
        }} />
        <ConfirmDialogHost />
        <AppShell />
      </AuthProvider>
    </ThemeProvider>
  );
}
